import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material";

import { logout } from "../../config/slices/user";

import { Logo } from "../index";
import { ThemeModeContext } from "../../config";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const {
    wallet_address,
    billing: { picture, name },
  } = useSelector((state) => state.user);

  const { mode, toggleThemeMode } = useContext(ThemeModeContext);
  const { palette } = useTheme();

  return (
    <AppBar>
      <Typography
        component={Link}
        to="/"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Logo />
      </Typography>

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
        <Typography variant="header_user" color="text.default">
          {name ||
            (wallet_address &&
              `${wallet_address?.substring(0, 5)}...${wallet_address?.substring(
                wallet_address?.length - 4
              )}`)}
        </Typography>

        <Button
          disableRipple
          sx={{
            padding: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          onClick={handleOpenUserMenu}
        >
          {picture ? (
            <Box
              component={"span"}
              sx={{
                width: 56,
                height: 56,
                backgroundImage: `url(${picture})`,
                backgroundSize: "cover",
                borderRadius: 14,
              }}
            />
          ) : (
            <SvgIcon
              viewBox="0 0 160 160"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 56, height: 56, fill: "none" }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M84.0545 159.898C126.306 157.781 159.999 122.77 159.999 79.9997C159.999 35.8697 124.13 0 79.9997 0C35.8697 0 0 35.8697 0 79.9997C0 122.612 33.4442 157.522 75.4759 159.873C76.9773 159.958 78.4853 160 79.9984 160C81.3549 160 82.7073 159.966 84.0545 159.898ZM132.594 124.362C142.723 112.372 148.837 96.8857 148.837 79.9997C148.837 42.0464 117.953 11.1628 79.9997 11.1628C42.0464 11.1628 11.1628 42.0464 11.1628 79.9997C11.1628 96.881 17.2727 112.364 27.397 124.352C30.0635 119.369 34.2947 114.836 39.8869 111.107C62.0636 96.3725 98.0076 96.3725 120.11 111.107C125.698 114.878 129.927 119.398 132.594 124.362ZM76.4698 148.747C77.6391 148.807 78.816 148.837 79.9997 148.837C81.0742 148.837 82.1429 148.812 83.2055 148.763C98.1979 148.066 112.521 142.495 124.054 132.838C122.715 128.298 119.142 123.907 113.859 120.335C95.5518 108.131 64.5194 108.131 46.0636 120.335C40.7799 123.907 37.2823 128.298 35.9427 132.838C47.3937 142.426 61.5942 147.986 76.4698 148.747ZM80.8959 91.3857H80.375H79.6308C62.7378 90.8648 50.0867 77.6927 50.0867 61.4695C50.0867 44.9486 63.5564 31.4789 80.0773 31.4789C96.5982 31.4789 110.068 44.9486 110.068 61.4695C109.994 77.7671 97.268 90.8648 81.1192 91.3857H80.8959ZM80.0029 42.5672C69.5843 42.5672 61.1751 51.0509 61.1751 61.3951C61.1751 71.5904 69.1378 79.8508 79.2587 80.2229C79.482 80.1485 80.2262 80.1485 80.9703 80.2229C90.9424 79.702 98.7563 71.516 98.8307 61.3951C98.8307 51.0509 90.4215 42.5672 80.0029 42.5672Z"
                fill="url(#paint0_linear_764_560)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_764_560"
                  x1="0"
                  y1="0"
                  x2="165.275"
                  y2="5.6488"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#DC70EE" />
                  <stop offset="1" stopColor="#6E3EF7" />
                  <stop offset="1" stopColor="#673AB7" />
                </linearGradient>
              </defs>
            </SvgIcon>
          )}

          <Box component={"span"} sx={{ ml: 2.5 }}>
            <SvgIcon
              viewBox="0 0 16 10"
              xmlns="http://www.w3.org/2000/svg"
              sx={{
                width: 16,
                height: 10,
                color: "transparent",
                transform: Boolean(anchorElUser)
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform ease .3s",
              }}
            >
              <path
                d="M15 1.5L8 8.5L1 1.5"
                stroke={palette.text.default}
                strokeOpacity="0.25"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </SvgIcon>
          </Box>
        </Button>

        <Menu
          sx={{ mt: 8 }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
        >
          <MenuItem component={Link} to={"/profile"} disableRipple>
            <ListItemIcon sx={{ minWidth: "0 !important", mr: "1rem" }}>
              <SvgIcon
                viewBox="0 0 24 24"
                fill="none"
                sx={{ stroke: "var(--default-text-color)", width: 24 }}
              >
                <path
                  d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M4.271 18.346S6.5 15.5 12 15.5s7.73 2.846 7.73 2.846M12 12a3 3 0 100-6 3 3 0 000 6z"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </SvgIcon>
            </ListItemIcon>
            <Typography variant="menu.item">Profile</Typography>
          </MenuItem>

          <MenuItem
            disableRipple
            onClick={() => {
              dispatch(logout());
              navigate("/signin");
            }}
          >
            <ListItemIcon sx={{ minWidth: "0 !important", mr: "1rem" }}>
              <SvgIcon
                viewBox="0 0 24 24"
                fill="none"
                sx={{ stroke: "var(--default-text-color)", width: 24 }}
              >
                <path
                  d="M12 12h7m0 0l-3 3m3-3l-3-3M19 6V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2v-1"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </SvgIcon>
            </ListItemIcon>
            <Typography variant="menu.item">Logout</Typography>
          </MenuItem>
        </Menu>

        <Button
          sx={{
            padding: 0,
            minWidth: 69,
            height: 54,
            ml: 2.5,
          }}
          onClick={toggleThemeMode}
        >
          <SvgIcon
            sx={{ width: 18, height: 18, color: palette.text.default }}
            viewBox="0 0 18 18"
            color="text.default"
          >
            {mode === "light" ? (
              <path d="M8.4742 1.38462V0.692308C8.4742 0.508696 8.54714 0.332605 8.67697 0.202772C8.8068 0.0729393 8.98289 0 9.1665 0C9.35012 0 9.52621 0.0729393 9.65604 0.202772C9.78587 0.332605 9.85881 0.508696 9.85881 0.692308V1.38462C9.85881 1.56823 9.78587 1.74432 9.65604 1.87415C9.52621 2.00398 9.35012 2.07692 9.1665 2.07692C8.98289 2.07692 8.8068 2.00398 8.67697 1.87415C8.54714 1.74432 8.4742 1.56823 8.4742 1.38462ZM9.1665 3.46154C8.0711 3.46154 7.0003 3.78636 6.0895 4.39494C5.1787 5.00351 4.46883 5.8685 4.04963 6.88052C3.63044 7.89254 3.52076 9.00614 3.73446 10.0805C3.94817 11.1549 4.47565 12.1417 5.25022 12.9163C6.02479 13.6909 7.01165 14.2183 8.086 14.432C9.16036 14.6457 10.274 14.5361 11.286 14.1169C12.298 13.6977 13.163 12.9878 13.7716 12.077C14.3801 11.1662 14.705 10.0954 14.705 9C14.7034 7.5316 14.1193 6.1238 13.081 5.08549C12.0427 4.04717 10.6349 3.46314 9.1665 3.46154ZM3.13823 3.95135C3.26814 4.08125 3.44433 4.15423 3.62804 4.15423C3.81176 4.15423 3.98794 4.08125 4.11785 3.95135C4.24776 3.82144 4.32073 3.64525 4.32073 3.46154C4.32073 3.27783 4.24776 3.10164 4.11785 2.97173L3.42554 2.27942C3.29564 2.14952 3.11945 2.07654 2.93573 2.07654C2.75202 2.07654 2.57583 2.14952 2.44593 2.27942C2.31602 2.40933 2.24304 2.58552 2.24304 2.76923C2.24304 2.95294 2.31602 3.12913 2.44593 3.25904L3.13823 3.95135ZM3.13823 14.0487L2.44593 14.741C2.31602 14.8709 2.24304 15.0471 2.24304 15.2308C2.24304 15.4145 2.31602 15.5907 2.44593 15.7206C2.57583 15.8505 2.75202 15.9235 2.93573 15.9235C3.11945 15.9235 3.29564 15.8505 3.42554 15.7206L4.11785 15.0283C4.18217 14.9639 4.2332 14.8876 4.26801 14.8035C4.30282 14.7195 4.32073 14.6294 4.32073 14.5385C4.32073 14.4475 4.30282 14.3574 4.26801 14.2734C4.2332 14.1893 4.18217 14.113 4.11785 14.0487C4.05353 13.9843 3.97717 13.9333 3.89312 13.8985C3.80908 13.8637 3.71901 13.8458 3.62804 13.8458C3.53708 13.8458 3.447 13.8637 3.36296 13.8985C3.27892 13.9333 3.20256 13.9843 3.13823 14.0487ZM14.705 4.15385C14.7959 4.15392 14.886 4.13607 14.97 4.10133C15.0541 4.06658 15.1304 4.01562 15.1948 3.95135L15.8871 3.25904C16.017 3.12913 16.09 2.95294 16.09 2.76923C16.09 2.58552 16.017 2.40933 15.8871 2.27942C15.7572 2.14952 15.581 2.07654 15.3973 2.07654C15.2136 2.07654 15.0374 2.14952 14.9075 2.27942L14.2152 2.97173C14.1182 3.06855 14.0522 3.19196 14.0254 3.32633C13.9987 3.46069 14.0124 3.59998 14.0648 3.72655C14.1173 3.85312 14.2061 3.96128 14.3201 4.03734C14.434 4.11341 14.568 4.15395 14.705 4.15385ZM15.1948 14.0487C15.0649 13.9187 14.8887 13.8458 14.705 13.8458C14.5213 13.8458 14.3451 13.9187 14.2152 14.0487C14.0853 14.1786 14.0123 14.3547 14.0123 14.5385C14.0123 14.7222 14.0853 14.8984 14.2152 15.0283L14.9075 15.7206C14.9718 15.7849 15.0482 15.8359 15.1322 15.8707C15.2162 15.9055 15.3063 15.9235 15.3973 15.9235C15.4882 15.9235 15.5783 15.9055 15.6624 15.8707C15.7464 15.8359 15.8228 15.7849 15.8871 15.7206C15.9514 15.6563 16.0024 15.5799 16.0372 15.4959C16.072 15.4118 16.09 15.3217 16.09 15.2308C16.09 15.1398 16.072 15.0497 16.0372 14.9657C16.0024 14.8816 15.9514 14.8053 15.8871 14.741L15.1948 14.0487ZM1.55112 8.30769H0.858812C0.6752 8.30769 0.499109 8.38063 0.369276 8.51046C0.239443 8.6403 0.166504 8.81639 0.166504 9C0.166504 9.18361 0.239443 9.3597 0.369276 9.48954C0.499109 9.61937 0.6752 9.69231 0.858812 9.69231H1.55112C1.73473 9.69231 1.91082 9.61937 2.04065 9.48954C2.17049 9.3597 2.24343 9.18361 2.24343 9C2.24343 8.81639 2.17049 8.6403 2.04065 8.51046C1.91082 8.38063 1.73473 8.30769 1.55112 8.30769ZM9.1665 15.9231C8.98289 15.9231 8.8068 15.996 8.67697 16.1258C8.54714 16.2557 8.4742 16.4318 8.4742 16.6154V17.3077C8.4742 17.4913 8.54714 17.6674 8.67697 17.7972C8.8068 17.9271 8.98289 18 9.1665 18C9.35012 18 9.52621 17.9271 9.65604 17.7972C9.78587 17.6674 9.85881 17.4913 9.85881 17.3077V16.6154C9.85881 16.4318 9.78587 16.2557 9.65604 16.1258C9.52621 15.996 9.35012 15.9231 9.1665 15.9231ZM17.4742 8.30769H16.7819C16.5983 8.30769 16.4222 8.38063 16.2924 8.51046C16.1625 8.6403 16.0896 8.81639 16.0896 9C16.0896 9.18361 16.1625 9.3597 16.2924 9.48954C16.4222 9.61937 16.5983 9.69231 16.7819 9.69231H17.4742C17.6578 9.69231 17.8339 9.61937 17.9637 9.48954C18.0936 9.3597 18.1665 9.18361 18.1665 9C18.1665 8.81639 18.0936 8.6403 17.9637 8.51046C17.8339 8.38063 17.6578 8.30769 17.4742 8.30769Z" />
            ) : (
              <path d="M18.133 11.4837C17.5627 13.3455 16.4197 14.9799 14.8667 16.1545C13.5032 17.1807 11.8806 17.8066 10.1812 17.9619C8.48177 18.1172 6.77267 17.7958 5.24576 17.0337C3.71885 16.2716 2.43456 15.099 1.53705 13.6476C0.639526 12.1962 0.164303 10.5233 0.164716 8.81678C0.15858 6.8249 0.806114 4.88602 2.00798 3.29759C3.18256 1.74458 4.81703 0.601593 6.67881 0.0312727C6.80151 -0.00651038 6.9322 -0.0101284 7.05681 0.0208078C7.18141 0.051744 7.29523 0.116064 7.38602 0.20685C7.4768 0.297636 7.54112 0.411454 7.57206 0.536062C7.603 0.660671 7.59938 0.791355 7.5616 0.914061C7.15449 2.26069 7.12036 3.69254 7.46283 5.05704C7.80531 6.42154 8.51156 7.66756 9.50633 8.66234C10.5011 9.65711 11.7471 10.3634 13.1116 10.7058C14.4761 11.0483 15.908 11.0142 17.2546 10.6071C17.3773 10.5693 17.508 10.5657 17.6326 10.5966C17.7572 10.6275 17.871 10.6919 17.9618 10.7826C18.0526 10.8734 18.1169 10.9873 18.1479 11.1119C18.1788 11.2365 18.1752 11.3672 18.1374 11.4899L18.133 11.4837Z" />
            )}
          </SvgIcon>
        </Button>
      </Box>
    </AppBar>
  );
};

export default Header;
